import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CONTACT } from "@/config/contact";
import SEO from "@/components/SEO";
import { CheckCircle2, MessageCircle, Home, Phone, Mail } from "lucide-react";

const VARIANTS = {
  contacto: {
    title: "Mensagem recebida.",
    subtitle: "Obrigada por escrever. Respondo pessoalmente no próprio dia útil.",
    next: [
      "Vou ler a sua mensagem com atenção.",
      "Contacto-a nas próximas horas úteis por email ou telefone.",
      "Se for urgente, use o WhatsApp — é a forma mais rápida.",
    ],
  },
  avaliacao: {
    title: "Pedido de avaliação recebido.",
    subtitle: "Vou preparar um relatório de valor de mercado — sem compromisso.",
    next: [
      "Analiso as transações recentes na sua rua/zona.",
      "Envio-lhe uma proposta de valor com fundamentação clara.",
      "Marcamos, se quiser, uma reunião para explicar o plano de marketing.",
    ],
  },
  comprador: {
    title: "Bem-vindo(a) à casa no porto.",
    subtitle: "Vou preparar uma primeira seleção alinhada com o que procura.",
    next: [
      "Analiso o seu perfil e os critérios que partilhou.",
      "Envio-lhe uma seleção inicial (2 a 5 imóveis) no dia útil seguinte.",
      "Se algo o(a) entusiasmar, marcamos visitas — com calma.",
    ],
  },
  vendedor: {
    title: "Obrigada por confiar em mim.",
    subtitle: "Preparo já a proposta de valor para o seu imóvel.",
    next: [
      "Analiso o mercado local e imóveis comparáveis.",
      "Envio-lhe o relatório e um plano de marketing.",
      "Combinamos, se fizer sentido, uma visita ao imóvel.",
    ],
  },
  chat: {
    title: "Recebi a sua mensagem.",
    subtitle: "Respondo no próprio dia útil.",
    next: [
      "Vou responder por email dentro de horas úteis.",
      "Se preferir mais rápido, siga por WhatsApp.",
    ],
  },
  newsletter: {
    title: "Subscrição confirmada.",
    subtitle: "Todas as segundas, um email curto com o que interessa no Grande Porto.",
    next: [
      "Nada de spam. Só imóveis e ideias úteis.",
      "Pode cancelar a qualquer momento, sem burocracia.",
    ],
  },
};

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const type = params.get("type") || "contacto";
  const variant = VARIANTS[type] || VARIANTS.contacto;

  return (
    <div data-testid="page-obrigado">
      <SEO title="Obrigada" description="Obrigada pelo seu contacto. Respondo pessoalmente no próximo dia útil." />
      <section className="container-cnp pt-20 lg:pt-28 pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#103C4A]/10 text-[#103C4A]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <span className="mt-6 tag-chip inline-flex">Recebido</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 leading-tight">{variant.title}</h1>
          <p className="mt-4 text-lg text-stone-600 leading-relaxed">{variant.subtitle}</p>
        </div>
      </section>

      <section className="container-cnp pb-16">
        <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="card-soft p-8">
            <div className="text-xs uppercase tracking-widest text-stone-500">Próximos passos</div>
            <ul className="mt-4 space-y-3">
              {variant.next.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#103C4A] text-white text-xs font-medium">{i + 1}</div>
                  <div className="text-stone-700">{step}</div>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-soft p-8">
            <div className="text-xs uppercase tracking-widest text-stone-500">Enquanto espera</div>
            <div className="mt-4 space-y-3">
              <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" data-testid="obrigado-whatsapp" className="flex items-center gap-3 rounded-lg border border-stone-200 p-4 hover:border-[#25D366] transition-colors">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]"><MessageCircle className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium text-stone-900 text-sm">Fale por WhatsApp</div>
                  <div className="text-xs text-stone-500">Resposta em minutos</div>
                </div>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 rounded-lg border border-stone-200 p-4 hover:border-[#103C4A] transition-colors">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]"><Phone className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium text-stone-900 text-sm">Ligar diretamente</div>
                  <div className="text-xs text-stone-500">{CONTACT.phone}</div>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 rounded-lg border border-stone-200 p-4 hover:border-[#103C4A] transition-colors">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]"><Mail className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium text-stone-900 text-sm">Enviar email</div>
                  <div className="text-xs text-stone-500 truncate">{CONTACT.email}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="container-cnp pb-24">
        <div className="max-w-3xl mx-auto card-soft p-10 text-center bg-white">
          <div className="text-xs uppercase tracking-widest text-stone-500">Continuar</div>
          <h2 className="mt-2 font-display text-2xl md:text-3xl text-stone-900">Explore enquanto respondo.</h2>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/imoveis" className="btn-primary"><Home className="h-4 w-4" /> Ver imóveis</Link>
            <Link to="/zonas" className="btn-secondary">Conhecer as zonas</Link>
            <Link to="/blog" className="btn-ghost">Ler o blog</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
